import type {
  ApiDataSourceRequest,
  DataSourceCandidatesRequest,
  DataSourceCandidatesResponse,
} from '@highimpact-frontend/api-types';
import { useSessionStorageString } from '@highimpact/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

// -- type definitions --

interface FetchDataSourcesOptions {
  tokenKey?: string;
  url?: string;
  request?: DataSourceCandidatesRequest;
}

interface FetchDataSourcesResponse {
  loading: boolean;
  error: boolean;
  data: DataSourceCandidatesResponse | null;
  /** @deprecated Use {@link FetchDataSourcesResponse.data} instead. */
  sources: DataSourceCandidatesResponse | null;
  refetch: () => Promise<void>;
}

const DEFAULT_LEGACY_REQUEST: ApiDataSourceRequest = {
  units: {
    coordinate_system: 'cartesian',
    unit_y: {
      units: 'all',
      grouping: ['site', 'unit'],
    },
  },
};

// -- helper functions --

function serializeRequest(request: DataSourceCandidatesRequest): string {
  return JSON.stringify(request);
}

/**
 * Loads data source candidates from the config API for legacy and v2 request shapes.
 */
export const useFetchDataSources = ({
  tokenKey = 'security_token',
  url = '/api/config/data-sources-candidates',
  request = DEFAULT_LEGACY_REQUEST,
}: FetchDataSourcesOptions = {}): FetchDataSourcesResponse => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<DataSourceCandidatesResponse | null>(null);
  const [token] = useSessionStorageString(tokenKey, '');
  const serializedRequest = useMemo(
    () => serializeRequest(request),
    [request]
  );

  const fetchDataSources = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: serializedRequest,
      });

      if (!response.ok) {
        throw new Error('Request error');
      }

      const payload = (await response.json()) as DataSourceCandidatesResponse;
      setData(payload);
    } catch (fetchError) {
      console.error('Something went wrong:', fetchError);
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [serializedRequest, token, url]);

  useEffect(() => {
    void fetchDataSources();
  }, [fetchDataSources]);

  return {
    loading,
    error,
    data,
    sources: data,
    refetch: fetchDataSources,
  };
};
